import { useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';
import {
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { connectProps } from '@devexpress/dx-react-core';
import { ViewState, EditingState, AppointmentModel } from '@devexpress/dx-react-scheduler';
import {
  Scheduler,
  Toolbar,
  MonthView,
  Appointments,
  AppointmentTooltip,
  AppointmentForm,
  DateNavigator,
  TodayButton,
  EditRecurrenceMenu,
} from '@devexpress/dx-react-scheduler-material-ui';

import {
  useAddCalendarMutation,
  useListCalendarsLazyQuery,
  useUpdateCalendarMutation,
  useDeleteCalendarMutation,
} from '../../../../../graphql/generated/graphql';
import { useProjectsState } from '../../../../hooks/projectsInfo';
import { useLoginState } from '../../../../hooks/loginInfo';

import {
  classes,
  StyledFab,
} from './config';
import AppointmentFormContainer from './formAppointment';
import TooltipContent from './tooltipAppointment';
import Appointment from './appointment';
import { theme } from '../../../../theme';
import { setAlertDescription } from '../../../../hooks/actions';

const currentDate = new Date();
const numberOfMlSeconds = currentDate.getTime();
const addMlSeconds = 60 * 60000;
const newDateObj = new Date(numberOfMlSeconds + addMlSeconds);

const CalendarPage = () => {
  const projectsState: any = useProjectsState();
  const currentProject = projectsState.projectId.get() || '';
  const [addCalendar] = useAddCalendarMutation();
  const [updateCalendar] = useUpdateCalendarMutation();
  const [deleteCalendar] = useDeleteCalendarMutation();
  const [getCalendar, { data: appointmentsCalendar, refetch }] = useListCalendarsLazyQuery({
    fetchPolicy: 'network-only',
  });

  const [canEditing] = useState(useLoginState().can_edit.get());
  const [appointments, setAppointments] = useState<AppointmentModel[]>([]);
  const [canModify] = useState(canEditing);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [editingFormVisible, setEditingFormVisible] = useState(false);
  const [isNewAppointment, setIsNewAppointment] = useState(false);
  const [deletedAppointmentId, setDeletedAppointmentId] = useState<string | undefined>(undefined);
  const [editingAppointment, setEditingAppointment] = useState<AppointmentModel | undefined>(undefined);
  const [previousAppointment, setPreviousAppointment] = useState<AppointmentModel | undefined>(undefined);
  const [startDayHour] = useState(0);
  const [addedAppointment, setAddedAppointment] = useState({});

  useEffect(() => {
    getCalendar({
      variables: {
        project_id: currentProject,
      },
    })
  }, []);

  useEffect(() => {
    if (appointmentsCalendar?.listCalendars) {
      const tempAppointments: any[] = appointmentsCalendar.listCalendars.map((n) => ({
        id: n?.id,
        title: n?.title,
        startDate: n?.start_date,
        endDate: n?.end_date,
        color: n?.color,
      }))
      setAppointments(tempAppointments)
      appointmentForm.update();
    }
  }, [appointmentsCalendar]);

  const addAppointments = async (_props: AppointmentModel) => {
    const {
      title,
      startDate,
      endDate,
      color,
    } = _props;
    try {
      const startDateAux = new Date(startDate.toString());
      const startDateBackUp = new Date(startDateAux)
      let endDateAux = new Date();
      startDateAux.setDate(startDateAux.getDate() + 1)
      if (endDate) {
        endDateAux = new Date(endDate.toString())
        if (startDateAux.getTime() === endDateAux.getTime()) { //know if one day of difference
          endDateAux = new Date(startDateBackUp.setHours(23, 59, 0));
        }
      }
      await addCalendar({
        variables: {
          project_id: currentProject,
          title: title || '',
          start_date: typeof startDate === 'object' ? startDate : new Date(currentDate.setHours(startDayHour, 0, 0)) || currentDate,
          end_date: typeof endDate === 'object' ? endDateAux : new Date(currentDate.setHours(startDayHour + 23, 59, 0)) || newDateObj,
          color: color || theme.palette.primary.main,
        },
      }).then((response) => {
        setAlertDescription({
          open: true,
          message: 'The activity has been added to your calendar',
          severity: 'success',
        });
        if (response.data?.addCalendar) {
          const tempAppointments = cloneDeep(appointments);
          tempAppointments.push({
            id: response.data?.addCalendar?.id || '',
            startDate: response.data?.addCalendar?.start_date || '',
            endDate: response.data?.addCalendar?.end_date || '',
            title: response.data?.addCalendar?.title || '',
            color: response.data?.addCalendar?.color || '',
          });
          setAppointments(tempAppointments);
        }
      });
    } catch (e) {
      console.error(e);
    }
  }

  const updateAppointments = async (_props: AppointmentModel, id: string) => {
    const {
      title,
      startDate,
      endDate,
      color,
    } = _props;
    try {
      await updateCalendar({
        variables: {
          calendar_id: id,
          title: title || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          color: color || undefined,
        },
      }).then((response) => {
        setAlertDescription({
          open: true,
          message: 'The activity has been updated',
          severity: 'success',
        })
      });
    } catch (e) {
      console.error(e);
    }
  }

  const deleteAppointments = async (appoimentsNext: AppointmentModel[], id: string) => {
    try {
      await deleteCalendar({
        variables: {
          calendar_id: id,
        },
      }).then((response) => {
        setAlertDescription({
          open: true,
          message: 'The activity has been deleted',
          severity: 'success',
        })
      });
      setAppointments(appoimentsNext);
      setDeletedAppointmentId(undefined);
      toggleConfirmationVisible();
    } catch (e) {
      console.error(e);
    }
  }

  const toggleConfirmationVisible = () => {
    setConfirmationVisible(!confirmationVisible);
  };

  const setDeletedAppointmentIdAction = (id: string) => {
    setDeletedAppointmentId(id);
  }

  const commitDeletedAppointment = () => {
    const nextData = appointments.filter((appointment: any) => appointment.id !== deletedAppointmentId);
    deleteAppointments(nextData, deletedAppointmentId || '')
  }

  const commitChanges = ({ added, changed, deleted }: any) => {
    if (added) {
      addAppointments(added);
    }
    if (changed) {
      const tempAppointments: AppointmentModel[] = [];
      appointments.forEach((appointment: any) => {
        if (changed[appointment.id]) {
          updateAppointments(changed[appointment.id], appointment.id)
          tempAppointments.push({ ...appointment, ...changed[appointment.id] })
        } else {
          tempAppointments.push(appointment)
        }
      });
      setAppointments(tempAppointments);
    }
    if (deleted !== undefined) {
      setDeletedAppointmentIdAction(deleted)
      toggleConfirmationVisible();
    }
  };

  const onAddedAppointmentChange = (addedAppointmentProps: any) => {
    setAddedAppointment(addedAppointmentProps);
    if (editingAppointment !== undefined) {
      setPreviousAppointment(editingAppointment || undefined)
    }
    setIsNewAppointment(true)
    setEditingAppointment(undefined)
  };

  const toggleEditingFormVisibility = () => {
    setEditingFormVisible(!editingFormVisible);
  };

  const onEditingAppointmentChange = (editingAppointmentProps: any) => {
    setEditingAppointment(editingAppointmentProps || undefined);
  };

  const cancelDelete = () => {
    toggleConfirmationVisible();
  };

  const appointmentForm = connectProps(AppointmentFormContainer, () => {
    const currentAppointment = appointments
      .filter((appointment: any) => editingAppointment && appointment.id === editingAppointment.id)[0]
      || addedAppointment;
    const cancelAppointment = () => {
      if (isNewAppointment) {
        setEditingAppointment(previousAppointment)
        setIsNewAppointment(false)
      }
    };

    return {
      visible: editingFormVisible,
      appointmentData: currentAppointment,
      commitChanges,
      visibleChange: toggleEditingFormVisibility,
      onEditingAppointmentChange,
      cancelAppointment,
    };
  });

  return (
    <>
      <Typography
        component="p"
        variant="h1"
        sx={{
          mb: 2,
          fontWeight: 500,
        }}
      >
        Calendar
      </Typography>
      <Typography
        component="p"
        sx={{
          mb: 2,
          fontWeight: 300
        }}
      >
        Add the key activities of your production
      </Typography>
      <Paper>
        <Scheduler
          data={appointments}
        >
          <ViewState
            defaultCurrentDate={currentDate}
          />
          <EditingState
            onCommitChanges={commitChanges}
            onEditingAppointmentChange={onEditingAppointmentChange}
            onAddedAppointmentChange={onAddedAppointmentChange}
          />
          <MonthView />
          <Toolbar />
          <DateNavigator />
          <TodayButton />
          <EditRecurrenceMenu />
          <Appointments
            appointmentContentComponent={Appointment}
          />
          <AppointmentTooltip
            contentComponent={TooltipContent}
            showOpenButton={canModify}
            showCloseButton
            showDeleteButton={canModify}
          />
          {canModify && (
            <AppointmentForm
              overlayComponent={appointmentForm}
              visible={editingFormVisible}
              onVisibilityChange={toggleEditingFormVisibility}
            />
          )}
        </Scheduler>
        <Dialog
          open={confirmationVisible}
          onClose={cancelDelete}
        >
          <DialogTitle
            sx={{
              fontSize: 20,
            }}
          >
            Delete Activity
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure that you want to remove this activity from your calendar?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={toggleConfirmationVisible}
              color="primary"
              variant="contained"
            >
              Cancel
            </Button>
            <Button
              onClick={commitDeletedAppointment}
              color="secondary"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {canModify && (
          <StyledFab
            color="primary"
            className={classes.addButton}
            onClick={() => {
              setEditingFormVisible(true);
              onEditingAppointmentChange(undefined);
              onAddedAppointmentChange({
                startDate: new Date(currentDate).setHours(startDayHour, 0, 0),
                endDate: new Date(currentDate).setHours(startDayHour + 24, 0, 0),
              });
            }}
          >
            <AddIcon />
          </StyledFab>
        )}
      </Paper>
    </>
  )
}

export default CalendarPage;
